import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useCurrentAccountId, useCurrentProjectId } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { useLocale } from '@adsk/alloy-react-locale';
import { useReducerStore } from '@adsk/acs-admin-ui-components-shared-components';
import {
  ACCESS_LEVELS,
  OP_STATUSES,
} from '@adsk/acs-admin-ui-components-helpers';
import { useDataApi } from '@adsk/alloy-helpers';
import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import CategorizedContent from './component';
import useProjectSettings from '../hooks/useProjectSettings';
import { useSourceTemplate } from './hooks/useSourceTemplate';
import { CategorizedContentStoreContext } from '../constants';
import {
  buildAccountsRequest,
  buildEditProjectRequest,
  buildGetProjectRequest,
  computeProjectDataChanged,
  isDuplicateNameError,
  massageAPIFields,
} from './utils';
import {
  cancelEdit,
  setIsEditing,
  setIsSaving,
  setMissingRequiredField,
  setProjectDataChanged,
  setState,
  setShowDuplicateNameError,
  setShowModalConfirmation,
  setShowProjectValueError,
  setSourceTemplate,
} from './store/actions';

const CategorizedContentContainer = ({
  disableCreateTemplateButton,
  loading: loadingParent,
  onProfileSave,
  projectUserAccess,
  showInformationBanner,
  showInProductSettings,
  showCreateTemplateButton,
  useGetConstructionApis,
  isEmsAccount,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { store, dispatch } = useReducerStore(CategorizedContentStoreContext);
  const latestOnProfileSave = useRef(onProfileSave);
  const {
    adminAccessProducts,
    dataGetProject,
    dataPatchProject,
    doFetchPatchProject,
    errorPatchProject,
    setDataGetProject,
  } = useProjectSettings(useGetConstructionApis);
  const projectId = useCurrentProjectId();
  const currentAccountId = useCurrentAccountId();
  const [currentAccount, setCurrentAccount] = useState(undefined);

  // Fetch project
  const {
    data: project,
    doFetch: doFetchProject,
    loading: isFetchingProjectData,
  } = useDataApi();
  useEffect(() => {
    if (projectId === undefined || useGetConstructionApis === undefined) return;
    doFetchProject(buildGetProjectRequest(projectId, useGetConstructionApis));
  }, [projectId, doFetchProject, useGetConstructionApis]);

  // Fetch Accounts
  const getAccountsRequest = useMemo(() => buildAccountsRequest(), []);
  const { data: accountsData, loading: isFetchingAccounts } = useDataApi(
    getAccountsRequest
  );
  // Find the current account
  useEffect(() => {
    if (!accountsData || !currentAccountId || currentAccount) return;
    const account = accountsData.results.find(
      (act) => act.id === currentAccountId
    );
    setCurrentAccount(account);
  }, [accountsData, currentAccountId, currentAccount]);

  const isProjectAdmin = projectUserAccess?.projectAdministrator;
  const isAccountAdmin = projectUserAccess?.accountAdministrator;
  const isAdminUser = isProjectAdmin || isAccountAdmin;
  const hasAccountAdminAccessLevel =
    currentAccount?.createProjectAccessLevels?.includes(
      ACCESS_LEVELS.ACCOUNT_ADMIN
    );
  const hasProjectAdminAccessLevel =
    currentAccount?.createProjectAccessLevels?.includes(
      ACCESS_LEVELS.PROJECT_ADMIN
    );
  const canCreateProjects = (isAccountAdmin && hasAccountAdminAccessLevel) ||
    (isProjectAdmin && hasProjectAdminAccessLevel)

  // Modal control
  const [isCreateTemplateModalOpen, setCreateTemplateModalOpen] = useState(false);
  const onSaveAsTemplateSuccess = useCallback(
    (template) => {
      setCreateTemplateModalOpen(false);
      const successUrl = `${getAccHostname()}/project-admin/template-settings/projects/${template.id}/permissions`
      window.location.assign(successUrl);
    },
    [setCreateTemplateModalOpen],
  );


  // Get source template
  const { loading: loadingSourceTemplate, sourceTemplateData } =
    useSourceTemplate(projectId);
  useEffect(() => {
    if (!loadingSourceTemplate && sourceTemplateData) {
      dispatch(setSourceTemplate(sourceTemplateData));
    }
  }, [loadingSourceTemplate, sourceTemplateData]);

  const loading =
    loadingParent ||
    dataGetProject == null ||
    adminAccessProducts == null ||
    loadingSourceTemplate
  const {
    ui: {
      missingRequiredField,
      projectDataChanged,
      showModalConfirmation,
      isEditing,
    },
    data: editedData,
    originalData,
  } = store;

  // Update onProfileSave reference
  useEffect(() => {
    latestOnProfileSave.current = onProfileSave;
  }, [onProfileSave]);

  // Set project data in state when fetched
  useEffect(() => {
    if (dataGetProject == null) {
      return;
    }

    dispatch(setState(dataGetProject));
  }, [dispatch, dataGetProject]);

  // compute mandatory fields
  useEffect(() => {
    let missing;
    if (isEmsAccount) {
      missing = !editedData.name;
    } else {
      missing = !editedData.name || !editedData.type;
    }

    if (missing !== missingRequiredField) {
      dispatch(setMissingRequiredField(missing));
    }
  }, [editedData.name, editedData.type, missingRequiredField, dispatch, isEmsAccount]);

  // compute project data changed
  useEffect(() => {
    if (editedData == null || originalData == null) return;

    const changed = computeProjectDataChanged(editedData, originalData);

    if (changed !== projectDataChanged) {
      dispatch(setProjectDataChanged(changed));
    }
  }, [editedData, originalData, projectDataChanged, dispatch]);

  // patch project success
  useEffect(() => {
    if (
      dataPatchProject == null ||
      dataGetProject == null ||
      setDataGetProject == null
    ) {
      return;
    }

    setDataGetProject({
      ...dataGetProject,
      ...dataPatchProject,
    });

    dispatch(setIsEditing(false));
    dispatch(setIsSaving(false));
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('SETTINGS_SUCCESS_MESSAGE'),
    });
    latestOnProfileSave.current({
      status: OP_STATUSES.SUCCESS,
      response: dataPatchProject,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPatchProject, setDataGetProject]);

  // patch project error
  useEffect(() => {
    if (errorPatchProject === null) return;

    dispatch(setIsSaving(false));
    // Intercept and display the duplicate name field error
    if (isDuplicateNameError(errorPatchProject.errors)) {
      dispatch(setShowDuplicateNameError(true));
    } else {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate('EDIT_PROJECT_PATCH_ERROR'),
      });
      latestOnProfileSave.current({
        status: OP_STATUSES.ERROR,
        response: errorPatchProject,
      });
    }
  }, [errorPatchProject, dispatch, T, showNotification]);

  const handleEditButtonClick = () => {
    dispatch(setIsEditing(true));
  };

  const handleCancelButtonClick = () => {
    if (projectDataChanged) {
      dispatch(setShowModalConfirmation(true));
    } else {
      dispatch(cancelEdit());
    }
  };

  const handleSaveButtonClick = () => {
    dispatch(setIsSaving(true));
    doFetchPatchProject(
      ...buildEditProjectRequest(
        projectId,
        massageAPIFields(
          editedData.asMutable({ deep: true }),
          originalData.asMutable({ deep: true })
        )
      )
    );
  };

  const handleModalConfirmationConfirm = () => {
    dispatch(setShowProjectValueError(false));
    dispatch(cancelEdit());
  };

  const handleModalConfirmationCancel = () => {
    dispatch(setShowModalConfirmation(false));
  };

  return (
    <CategorizedContent
      account={currentAccount}
      adminAccessProducts={adminAccessProducts}
      disableCreateTemplateButton={disableCreateTemplateButton}
      isAdminUser={isAdminUser}
      isEditing={isEditing}
      isEmsAccount={isEmsAccount}
      loading={loading}
      onCancelButtonClick={handleCancelButtonClick}
      onEditButtonClick={handleEditButtonClick}
      onModalConfirmationCancel={handleModalConfirmationCancel}
      onModalConfirmationConfirm={handleModalConfirmationConfirm}
      onSaveButtonClick={handleSaveButtonClick}
      onSaveAsTemplateClose={() => setCreateTemplateModalOpen(false)}
      onSaveAsTemplateSuccess={onSaveAsTemplateSuccess}
      onSaveAsTemplateButtonClick={() => setCreateTemplateModalOpen(true)}
      project={project}
      showInformationBanner={showInformationBanner}
      showInProductSettings={showInProductSettings}
      showModalConfirmation={showModalConfirmation}
      showCreateTemplateButton={showCreateTemplateButton && canCreateProjects}
      showCreateTemplateModal={isCreateTemplateModalOpen}
    />
  );
};

CategorizedContentContainer.propTypes = {
  disableCreateTemplateButton: PropTypes.bool,
  loading: PropTypes.bool,
  onProfileSave: PropTypes.func,
  projectUserAccess: PropTypes.object,
  showInformationBanner: PropTypes.bool,
  showInProductSettings: PropTypes.bool,
  showCreateTemplateButton: PropTypes.bool,
  useGetConstructionApis: PropTypes.bool,
  isEmsAccount: PropTypes.bool,
};

CategorizedContentContainer.defaultProps = {
  isAdminUser: false,
  isEmsAccount: false,
  showInProductSettings: false,
};

export default CategorizedContentContainer;
